import { useState, useEffect, useRef } from 'react';

/**
 * A custom hook to handle global search behavior across the admin dashboard.
 * Features:
 * - Manages search input state
 * - Automatically debounces the search query by the specified delay
 * - Attaches a global keyboard listener to auto-focus the search bar when typing
 * 
 * @param {string} initialValue - Initial value of the search query
 * @param {number} delay - Debounce delay in milliseconds
 * @param {function} onSearchChange - Optional callback triggered immediately when user types
 */
const useGlobalSearch = (initialValue = '', delay = 400, onSearchChange = null) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialValue);
  const searchInputRef = useRef(null);

  // Handle immediate state update and optional callback
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  // Debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [searchQuery, delay]);

  // Global keydown listener to auto-focus search bar
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is already typing in an input or textarea
      const activeTag = document.activeElement?.tagName;
      if (
        activeTag === 'INPUT' || 
        activeTag === 'TEXTAREA' || 
        document.activeElement?.isContentEditable
      ) {
        return;
      }
      
      // If it's a regular character key press (length 1) and no modifiers are held
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    searchQuery,
    setSearchQuery: handleSearchChange,
    debouncedSearchQuery,
    searchInputRef
  };
};

export default useGlobalSearch;
